import { useRef, useEffect, useState } from 'react';
import BankIcon from 'assets/icons/bank-with-stroke.svg?react';
import { Loader } from 'components';
import CardWithIcon from 'components/CardWithIcon/CardWithIcon';
import useNavigateByIdentificationStatus from 'hooks/useNavigateByIdentificationStatus';
import useBankIdAccountSelection from 'pages/Registration/BankId/BankIdAccountSelection/useBankIdAccountSelection';
import { TwButton } from 'components/TwButton/TwButton';
import { useTranslation } from 'react-i18next';
import { formatBankAccount } from 'utils/bankAccount';

function BankIdAccountSelection() {
  const { t } = useTranslation();
  const { navigateByIdentificationStatus } =
    useNavigateByIdentificationStatus('bankId');
  const [preRenderConditionsChecked, setPreRenderConditionsChecked] =
    useState(false);
  const {
    accountList,
    isLoading,
    isPending,
    isFetchedAccountList,
    selectedAccount,
    handleSelectAccount,
    confirmBankIdAccountNumber,
    rejectBankIdAccountNumber,
  } = useBankIdAccountSelection();

  // Ref to be used inside useEffect to skip bank account list
  const refAccountList = useRef(accountList);
  useEffect(() => {
    refAccountList.current = accountList;
  }, [accountList]);

  // prerender condition: Skip screen if account list is empty
  useEffect(() => {
    if (!isFetchedAccountList) return;

    // Timeout to wait useBankIdAccountSelection hook to update the state variable with the bank response
    setTimeout(() => {
      const isEmptyList =
        !refAccountList.current || refAccountList.current.length === 0;
      if (isEmptyList) {
        navigateByIdentificationStatus();
      } else {
        setPreRenderConditionsChecked(true);
      }
    }, 250);
  }, [isFetchedAccountList]);

  if (!preRenderConditionsChecked) return <Loader />;

  return (
    <>
      <div className="flex flex-col">
        <CardWithIcon
          icon={<BankIcon data-test="bankId-bank-icon" />}
          heading={t('bank_id_account_selection:bank_details')}
          dataTest="bankId-card"
          className="rounded-none lg:rounded border-none lg:border-solid"
        >
          <p>
            {accountList.length === 1
              ? t('bank_id_account_selection:info_text_single_account')
              : t('bank_id_account_selection:info_text_multiaccount')}
          </p>

          {accountList.length === 1 && (
            <div
              className="text-primary text-3xl font-semibold bg-background-accent p-5 text-center w-full"
              data-test="single-account"
            >
              {accountList[0]}
            </div>
          )}

          {accountList.length > 1 && (
            <div className="w-full flex flex-col gap-2">
              {accountList.map((account, index) => (
                <button
                  className={`items-center border rounded flex py-4 pl-4 bg-transparent transition-colors w-full hover:bg-background-accent ${
                    account === selectedAccount ? 'border-primary' : ''
                  }`}
                  onClick={() => handleSelectAccount(account)}
                  data-test={`account-${index}`}
                  type="button"
                  key={account}
                >
                  <div className="flex items-center gap-6">
                    <input
                      type="radio"
                      id={account}
                      name={account}
                      value={selectedAccount}
                      checked={selectedAccount === account}
                      onChange={() => handleSelectAccount(account)}
                      className="appearance-none rounded-full h-4 w-4 border checked:ring-4 checked:ring-offset-2 checked:ring-primary checked:border-0"
                    />
                    <label htmlFor={account} className="flex flex-col">
                      <div className="text-left font-light">
                        {t('bank_id_account_selection:account_label')}
                      </div>
                      <div className="font-bold">
                        {formatBankAccount(account)}
                      </div>
                    </label>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="mx-auto lg:mx-0 w-full flex justify-center lg:justify-start">
            <TwButton
              disabled={!selectedAccount || isLoading || isPending}
              onClick={confirmBankIdAccountNumber}
              data-test="confirm"
              pending={isPending}
            >
              {accountList.length === 1
                ? t('bank_id_account_selection:single_account_button')
                : t('bank_id_account_selection:info_text_multiaccount_button')}
            </TwButton>
          </div>
        </CardWithIcon>

        <button
          type="button"
          className="text-primary mx-auto lg:mx-0 mb-10 hover:underline text-left w-fit"
          onClick={rejectBankIdAccountNumber}
          data-test="reject"
          disabled={isLoading}
        >
          {t('bank_id_account_selection:reject_text')}
        </button>
      </div>
    </>
  );
}

export default BankIdAccountSelection;
